import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "../../utils/notify";
import {
  CubeIcon,
  CloudIcon,
  ServerIcon,
  CircleStackIcon,
  LinkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  BoltIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  UserIcon,
  UsersIcon,
  TagIcon,
  CalendarIcon,
  DocumentDuplicateIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { ConnectorsTab } from "./ConnectorsTab";
import { useAuth } from "../../context/AuthContext";
import {
  classifyAssetApi,
  getAssetDashboardAgentApi,
  getAssetOwnershipApi,
  getAssetStatsApi,
  getConnectorsCategoryApi,
  getConnectorStatsApi,
  getInventoryConnectorsApi,
} from "../../services/axios/AuthApis/AssetManagementApis";
import { AssetsTab } from "./InventoryTab";
import { DashboardTab } from "./DashboardTab";
import ClassificationTab from "./ClassificationTab";
import OwnershipTab from "./OwnershipTab";
import { exportAssetsCSV, exportAssetsExcel } from "../../utils/exportAssets";
import { useSelector } from "react-redux";
import Layout from "../Layout";
import { Link } from "lucide-react";
import TabNavigation from "../ui/TabNavigation";
import { PageLoader } from "../ui";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AssetManagement = () => {
  const { user, token } = useAuth();
  const isCustomerAdmin = user.role === "customer_admin";
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const isSecurityAnalyst =
    user.role === "security_analyst" && selectedCompany ? true : false;
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") ||
      (isCustomerAdmin || isSecurityAnalyst ? "dashboard" : "connectors"),
  );

  // Lets a notification (or any other external link) deep-link into a
  // specific tab via ?tab=inventory, same convention CSPM already uses.
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  const [dashboardFilter, setDashboardFilter] = useState("all");
  const [globalFilters, setGlobalFilters] = useState({
    provider: "",
    assetType: "",
    criticality: "",
    status: "",
    owner: "",
    region: "",
    publicExposure: "",
  });
  const [dateRange, setDateRange] = useState("30d");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCriticality, setFilterCriticality] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOwner, setFilterOwner] = useState("");
  const [categories, setCategories] = useState([]);
  //   const [filteredConnectors, setFilteredConnectors] = useState([]);
  //   const [selectedCategory, setSelectedCategory] = useState("");

  // Data states
  const [assets, setAssets] = useState([]);
  //   const [connectors, setConnectors] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });
  const [assetsPagination, setAssetsPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
  });

  // Modal states
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [showAssetDetailModal, setShowAssetDetailModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetFormData, setAssetFormData] = useState({
    name: "",
    type: "IT Infrastructure",
    category: "",
    owner: "",
    business_unit: "",
    criticality: "Medium",
    status: "Active",
    tags: [],
    metadata: {},
  });
  const [connectorFormData, setConnectorFormData] = useState({
    type: "AWS",
    name: "",
    config: {},
    sync_schedule: "daily",
  });
  const [connectorsStats, setConnectorsStats] = useState(null);
  const [classifiedAsset, setClassifiedAsset] = useState([]);
  const [isClassifying, setIsClassifying] = useState(false);
  const [ownership, setOwnership] = useState([]);
  const [ownershipLoading, setOwnershipLoading] = useState(false);

  // Asset type definitions from backend
  const assetTypes = [
    "IT Infrastructure",
    "Cloud",
    "HR & Workforce",
    "Developer Tools",
    "Messaging & Collaboration",
    "SaaS & Business Apps",
  ];

  const assetCategories = {
    "IT Infrastructure": [
      "Servers",
      "Network Devices",
      "Firewalls",
      "Endpoints",
    ],
    Cloud: ["AWS Resources", "Azure Resources", "GCP Resources"],
    "HR & Workforce": ["Employee Accounts", "HRMS Systems", "Identity Records"],
    "Developer Tools": ["GitHub Repos", "GitLab Projects", "Jenkins Pipelines"],
    "Messaging & Collaboration": ["Slack", "Teams", "Zoom", "Email Systems"],
    "SaaS & Business Apps": [
      "Salesforce",
      "Workday",
      "ServiceNow",
      "Jira",
      "Confluence",
    ],
  };

  const connectorTypes = [
    {
      type: "AWS",
      name: "Amazon Web Services",
      description: "Auto-import EC2, S3, RDS, IAM roles",
    },
    {
      type: "Azure",
      name: "Microsoft Azure",
      description: "Auto-import VMs, Storage, AD accounts",
    },
    {
      type: "GCP",
      name: "Google Cloud Platform",
      description: "Auto-import GCE, GCS, IAM",
    },
    {
      type: "ServiceNow",
      name: "ServiceNow CMDB",
      description: "Sync IT assets from CMDB",
    },
    {
      type: "Okta",
      name: "Okta Identity",
      description: "Sync workforce identity records",
    },
    {
      type: "Jira",
      name: "Atlassian Jira",
      description: "Sync projects & linked components",
    },
    {
      type: "Slack",
      name: "Slack Workspace",
      description: "Sync workspaces + channels",
    },
    {
      type: "GitHub",
      name: "GitHub",
      description: "Sync repos, orgs, and pipelines",
    },
    {
      type: "GitLab",
      name: "GitLab",
      description: "Sync GitLab projects and pipelines",
    },
  ];

  const customerId = useMemo(() => {
    return isCustomerAdmin
      ? user?.id
      : isSecurityAnalyst && selectedCompany
        ? selectedCompany?.user_id
        : null;
  }, [isCustomerAdmin, isSecurityAnalyst, user?.id, selectedCompany]);

  console.log({ customerId: customerId });
  useEffect(() => {
    loadAssets(assetsPagination.page, assetsPagination.page_size, searchTerm);
    loadConnectorsStats();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categories = await getConnectorsCategoryApi();
      setCategories(categories.results);
    } catch (error) {
      console.error("Failed to load connectors:", error);
      // setConnectors([]);
    }
  };

  useEffect(() => {
    loadAssets(assetsPagination.page, assetsPagination.page_size, searchTerm);
  }, [searchTerm, filterCategory, filterCriticality, filterStatus]);

  const apiCall = async (endpoint, method = "GET", data = null) => {
    try {
      const config = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (data && method !== "GET") {
        config.body = JSON.stringify(data);
      }

      const response = await fetch(`${BACKEND_URL}${endpoint}`, config);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      toast.error(`API Error: ${error.message}`);
      throw error;
    }
  };

  const loadDashboardData = useCallback(async () => {
    try {
      if (!customerId) return;

      const agentData = await getAssetDashboardAgentApi(customerId).catch(
        () => null,
      );
      if (agentData?.stats) {
        setDashboardStats({
          ...agentData.stats,
          agent: agentData.agent,
        });
        setLastUpdated(new Date());
        return;
      }

      const stats = await getAssetStatsApi(customerId);
      setDashboardStats(stats.stats || stats);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    }
  }, [customerId]);

  const clearGlobalFilters = () => {
    setGlobalFilters({
      provider: "",
      assetType: "",
      criticality: "",
      status: "",
      owner: "",
      region: "",
      publicExposure: "",
    });
    setDashboardFilter("all");
  };

  const updateGlobalFilter = (key, value) => {
    setGlobalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setDashboardFilter("all");
  };

  const handleKpiDrillDown = (type) => {
    setActiveTab("dashboard");
    setDashboardFilter(type);

    if (type === "critical") {
      setGlobalFilters((prev) => ({ ...prev, criticality: "high" }));
    }
    if (type === "public") {
      setGlobalFilters((prev) => ({ ...prev, publicExposure: "true" }));
    }
    if (type === "sensitive") {
      setGlobalFilters((prev) => ({ ...prev, publicExposure: "false" }));
    }
    if (type === "unknown") {
      setGlobalFilters((prev) => ({ ...prev, owner: "Unassigned" }));
    }
  };

  const refreshDashboard = useCallback(() => {
    if (isCustomerAdmin || isSecurityAnalyst) {
      loadDashboardData();
    }
  }, [isCustomerAdmin, isSecurityAnalyst, loadDashboardData]);

  useEffect(() => {
    if (!isCustomerAdmin && !isSecurityAnalyst) return;

    refreshDashboard();
    const interval = setInterval(refreshDashboard, 60000);
    return () => clearInterval(interval);
  }, [isCustomerAdmin, isSecurityAnalyst, refreshDashboard]);

  const loadConnectorsStats = async () => {
    try {
      const stats = await getConnectorStatsApi();
      setConnectorsStats(stats.stats);
    } catch (error) {
      console.error("Failed to load connector stats:", error);
    }
  };

  const loadAssets = async (page, pageSize, search) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (customerId) params.append("customer_id", customerId);
      if (search) params.append("search", search);
      if (filterCategory) params.append("category", filterCategory);
      if (filterCriticality) params.append("criticality", filterCriticality);
      if (filterStatus) params.append("status", filterStatus);
      if (filterOwner) params.append("owner", filterOwner);

      const data = await getInventoryConnectorsApi(params);

      setAssets(data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: data.total || 0,
        hasMore: data.has_more || false,
      }));

      setAssetsPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages,
      });
    } catch (error) {
      console.error("Failed to load assets:", error);
      setAssets([]);

      setAssetsPagination({
        page: 1,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssetsPageChange = (newPage, newPageSize) => {
    const pageSize =
      newPageSize !== undefined ? newPageSize : assetsPagination.page_size;
    setLoading(true);
    loadAssets(newPage, pageSize, searchTerm);
  };

  const loadConnectors = async () => {
    try {
      const connectorsData = await apiCall("/api/connectors/all/");
      // Fix: Ensure we always set an array
      const connectorsArray = Array.isArray(connectorsData)
        ? connectorsData
        : connectorsData.results || [];
      // setConnectors(connectorsArray);
    } catch (error) {
      console.error("Failed to load connectors:", error);
      // setConnectors([]);
    }
  };

  const handleCreateAsset = async () => {
    try {
      await apiCall("/api/assets", "POST", assetFormData);
      toast.success("Asset created successfully");
      setShowAssetModal(false);
      resetAssetForm();
      loadAssets();
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to create asset");
    }
  };

  const handleUpdateAsset = async (assetId, updateData) => {
    try {
      await apiCall(`/api/assets/${assetId}`, "PUT", updateData);
      toast.success("Asset updated successfully");
      loadAssets();
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update asset");
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (window.confirm("Are you sure you want to delete this asset?")) {
      try {
        await apiCall(`/api/assets/${assetId}`, "DELETE");
        toast.success("Asset deleted successfully");
        loadAssets();
        loadDashboardData();
      } catch (error) {
        toast.error("Failed to delete asset");
      }
    }
  };

  const handleCreateConnector = async () => {
    try {
      await apiCall("/api/connectors", "POST", connectorFormData);
      toast.success("Connector created successfully");
      setShowConnectorModal(false);
      resetConnectorForm();
      loadConnectors();
    } catch (error) {
      toast.error("Failed to create connector");
    }
  };

  const handleSyncConnector = async (connectorId) => {
    try {
      toast.loading("Starting sync...", { id: "sync" });
      const result = await apiCall(
        `/api/connectors/${connectorId}/sync`,
        "POST",
      );
      toast.success(
        `Sync completed: ${result.result.new_assets} new, ${result.result.updated_assets} updated`,
        { id: "sync" },
      );
      loadAssets();
      loadConnectors();
      loadDashboardData();
    } catch (error) {
      toast.error("Sync failed", { id: "sync" });
    }
  };

  // const handleExportAssets = async (format = "csv") => {
  //   try {
  //     toast.loading("Generating export...", { id: "export" });
  //     const response = await fetch(
  //       `${BACKEND_URL}/api/assets/export?format=${format}`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       },
  //     );

  //     if (response.ok) {
  //       const blob = await response.blob();
  //       const url = window.URL.createObjectURL(blob);
  //       const a = document.createElement("a");
  //       a.href = url;
  //       a.download = `assets_export_${
  //         new Date().toISOString().split("T")[0]
  //       }.${format}`;
  //       document.body.appendChild(a);
  //       a.click();
  //       document.body.removeChild(a);
  //       window.URL.revokeObjectURL(url);
  //       toast.success("Export downloaded", { id: "export" });
  //     } else {
  //       throw new Error("Export failed");
  //     }
  //   } catch (error) {
  //     toast.error("Export failed", { id: "export" });
  //   }
  // };

  const handleExportAssets = (type) => {
    if (!assets || assets.length === 0) {
      toast.error("No assets available to export");
      return;
    }

    if (type === "csv") {
      exportAssetsCSV(assets);
    }

    if (type === "excel") {
      exportAssetsExcel(assets);
    }
  };
  const viewAssetDetails = async (assetId) => {
    try {
      const assetDetails = await apiCall(`/api/assets/${assetId}`);
      setSelectedAsset(assetDetails);
      setShowAssetDetailModal(true);
    } catch (error) {
      toast.error("Failed to load asset details");
    }
  };

  const resetAssetForm = () => {
    setAssetFormData({
      name: "",
      type: "IT Infrastructure",
      category: "",
      owner: "",
      business_unit: "",
      criticality: "Medium",
      status: "Active",
      tags: [],
      metadata: {},
    });
  };

  const resetConnectorForm = () => {
    setConnectorFormData({
      type: "AWS",
      name: "",
      config: {},
      sync_schedule: "daily",
    });
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case "HIGH":
        return "text-red-600 bg-red-50 border-red-200";
      case "MEDIUM":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "LOW":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case true:
        return "text-green-600 bg-green-50 border-green-200";
      case false:
        return "text-red-600 bg-red-50 border-gray-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "IT Infrastructure":
        return <ServerIcon className="w-5 h-5" />;
      case "Cloud":
        return <CloudIcon className="w-5 h-5" />;
      case "HR & Workforce":
        return <UserIcon className="w-5 h-5" />;
      case "Developer Tools":
        return <DocumentDuplicateIcon className="w-5 h-5" />;
      case "Messaging & Collaboration":
        return <GlobeAltIcon className="w-5 h-5" />;
      case "SaaS & Business Apps":
        return <BuildingOfficeIcon className="w-5 h-5" />;
      default:
        return <CubeIcon className="w-5 h-5" />;
    }
  };

  // Connectors Tab Component
  <ConnectorsTab />;

  const assetInventory = useMemo(() => {
    const inventory = dashboardStats?.asset_inventory || [];

    return inventory.filter((asset) => {
      const providerMatch =
        !globalFilters.provider ||
        (asset.provider || "").toLowerCase() ===
          globalFilters.provider.toLowerCase();

      const assetTypeMatch =
        !globalFilters.assetType ||
        (asset.asset_type || "").toLowerCase() ===
          globalFilters.assetType.toLowerCase();

      const criticalityMatch =
        !globalFilters.criticality ||
        (asset.criticality || "").toLowerCase() ===
          globalFilters.criticality.toLowerCase();

      const statusMatch =
        !globalFilters.status ||
        String(asset.status || "active").toLowerCase() ===
          globalFilters.status.toLowerCase();

      const ownerMatch =
        !globalFilters.owner ||
        (asset.owner || asset.metadata?.owner || "Unassigned")
          .toString()
          .toLowerCase()
          .includes(globalFilters.owner.toLowerCase());

      const regionMatch =
        !globalFilters.region ||
        (asset.region || "").toLowerCase() ===
          globalFilters.region.toLowerCase();

      const publicExposureMatch =
        globalFilters.publicExposure === "" ||
        String(Boolean(asset.public_access)).toLowerCase() ===
          globalFilters.publicExposure.toLowerCase();

      return (
        providerMatch &&
        assetTypeMatch &&
        criticalityMatch &&
        statusMatch &&
        ownerMatch &&
        regionMatch &&
        publicExposureMatch
      );
    });
  }, [dashboardStats, globalFilters]);

  const activeFilterEntries = Object.entries(globalFilters).filter(
    ([, value]) => Boolean(value),
  );

  const filterOptions = useMemo(() => {
    const inventory = dashboardStats?.asset_inventory || [];

    return {
      provider: [
        ...new Set(inventory.map((asset) => asset.provider).filter(Boolean)),
      ].sort(),
      assetType: [
        ...new Set(inventory.map((asset) => asset.asset_type).filter(Boolean)),
      ].sort(),
      criticality: [
        ...new Set(inventory.map((asset) => asset.criticality).filter(Boolean)),
      ]
        .map((item) => item.toLowerCase())
        .sort(),
      status: [
        ...new Set(inventory.map((asset) => asset.status).filter(Boolean)),
      ].sort(),
      owner: [
        ...new Set(
          inventory
            .map(
              (asset) => asset.owner || asset.metadata?.owner || "Unassigned",
            )
            .filter(Boolean),
        ),
      ].sort(),
      region: [
        ...new Set(inventory.map((asset) => asset.region).filter(Boolean)),
      ].sort(),
    };
  }, [dashboardStats]);

  const selectedAssetDetail = useMemo(() => {
    if (!dashboardStats || !expandedAssetId) {
      return null;
    }

    const assetInventoryData = assetInventory.length
      ? assetInventory
      : dashboardStats.asset_inventory || [];
    return (
      assetInventoryData.find((asset) => {
        const assetId =
          asset.asset_id || `${asset.asset_name}-${asset.provider}`;
        return assetId === expandedAssetId;
      }) || null
    );
  }, [dashboardStats, expandedAssetId, assetInventory]);

  // Main render
  if (loading && !dashboardStats) {
    return <PageLoader title="Loading Asset Management..." />;
  }

  return (
    <div className="py-6 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          AI Asset Manager
        </h1>
        <p className="text-gray-600">
          AI-powered asset management with intelligent classification and
          ownership tracking.
        </p>
      </div>

      {connectorsStats && !isCustomerAdmin && !isSecurityAnalyst && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CubeIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Connectors
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {connectorsStats.total_connectors}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />

              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Enabled</p>
                <p className="text-2xl font-bold text-gray-900">
                  {connectorsStats.total_active_connectors}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Squares2X2Icon className="w-8 h-8 text-green-600" />

              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">
                  {connectorsStats.total_categories}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <BuildingOfficeIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Integration Types
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {connectorsStats.total_connector_types}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Layout */}
      {(() => {
        const tabs = [
          ...(isCustomerAdmin || isSecurityAnalyst
            ? [{ id: "dashboard", name: "Dashboard", icon: ChartBarIcon }]
            : []),
          { id: "connectors", name: "Connectors", icon: LinkIcon },
          ...(isCustomerAdmin || isSecurityAnalyst
            ? [{ id: "inventory", name: "Inventory", icon: CubeIcon }]
            : []),
        ];

        return (
          <>
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            <AnimatePresence mode="wait">
              {activeTab === "dashboard" &&
                (isCustomerAdmin || isSecurityAnalyst) && (
                  <motion.div
                    key="dashboard"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DashboardTab
                      dashboardStats={dashboardStats}
                      dashboardFilter={dashboardFilter}
                      setDashboardFilter={setDashboardFilter}
                      setGlobalFilters={setGlobalFilters}
                      clearGlobalFilters={clearGlobalFilters}
                      updateGlobalFilter={updateGlobalFilter}
                      refreshDashboard={refreshDashboard}
                      setActiveTab={setActiveTab}
                      expandedAssetId={expandedAssetId}
                      setExpandedAssetId={setExpandedAssetId}
                      assetInventory={assetInventory}
                    />
                  </motion.div>
                )}
              {activeTab === "inventory" && (
                <motion.div
                  key="inventory"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <AssetsTab
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filterCategory={filterCategory}
                    setFilterCategory={setFilterCategory}
                    filterCriticality={filterCriticality}
                    setFilterCriticality={setFilterCriticality}
                    filterStatus={filterStatus}
                    setFilterStatus={setFilterStatus}
                    setShowAssetModal={setShowAssetModal}
                    handleExportAssets={handleExportAssets}
                    loading={loading}
                    assets={assets}
                    getTypeIcon={getTypeIcon}
                    getCriticalityColor={getCriticalityColor}
                    getStatusColor={getStatusColor}
                    viewAssetDetails={viewAssetDetails}
                    setAssetFormData={setAssetFormData}
                    setSelectedAsset={setSelectedAsset}
                    user={user}
                    handleDeleteAsset={handleDeleteAsset}
                    pagination={pagination}
                    setPagination={setPagination}
                    assetCategories={assetCategories}
                    categories={categories}
                    loadAssets={loadAssets}
                    assetsPagination={assetsPagination}
                    setAssetsPagination={setAssetsPagination}
                    handleAssetsPageChange={handleAssetsPageChange}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                  />
                </motion.div>
              )}
              {activeTab === "connectors" && (
                <motion.div
                  key="connectors"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <ConnectorsTab />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        );
      })()}

      {/* Asset Creation/Edit Modal */}
      <AnimatePresence>
        {showAssetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedAsset ? "Edit Asset" : "Create New Asset"}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAssetModal(false);
                      setSelectedAsset(null);
                      resetAssetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset Name *
                      </label>
                      <input
                        type="text"
                        value={assetFormData.name}
                        onChange={(e) =>
                          setAssetFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter asset name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asset Type *
                      </label>
                      <select
                        value={assetFormData.type}
                        onChange={(e) => {
                          setAssetFormData((prev) => ({
                            ...prev,
                            type: e.target.value,
                            category: "",
                          }));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {assetTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={assetFormData.category}
                        onChange={(e) =>
                          setAssetFormData((prev) => ({
                            ...prev,
                            category: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select category</option>
                        {assetFormData.type &&
                          assetCategories[assetFormData.type] &&
                          assetCategories[assetFormData.type].map(
                            (category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ),
                          )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Owner (Email) *
                      </label>
                      <input
                        type="email"
                        value={assetFormData.owner}
                        onChange={(e) =>
                          setAssetFormData((prev) => ({
                            ...prev,
                            owner: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="owner@company.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Unit *
                      </label>
                      <input
                        type="text"
                        value={assetFormData.business_unit}
                        onChange={(e) =>
                          setAssetFormData((prev) => ({
                            ...prev,
                            business_unit: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Engineering, Sales, etc."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Criticality *
                      </label>
                      <select
                        value={assetFormData.criticality}
                        onChange={(e) =>
                          setAssetFormData((prev) => ({
                            ...prev,
                            criticality: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={assetFormData.status}
                      onChange={(e) =>
                        setAssetFormData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Active">Active</option>
                      <option value="Retired">Retired</option>
                      <option value="In-Review">In-Review</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={assetFormData.tags.join(", ")}
                      onChange={(e) =>
                        setAssetFormData((prev) => ({
                          ...prev,
                          tags: e.target.value
                            .split(",")
                            .map((tag) => tag.trim())
                            .filter((tag) => tag),
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="production, critical, web-server"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                  <button
                    onClick={() => {
                      setShowAssetModal(false);
                      setSelectedAsset(null);
                      resetAssetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={
                      selectedAsset
                        ? () =>
                            handleUpdateAsset(selectedAsset.id, assetFormData)
                        : handleCreateAsset
                    }
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {selectedAsset ? "Update Asset" : "Create Asset"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Connector Configuration Modal */}
      <AnimatePresence>
        {showConnectorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-lg w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Configure Connector
                  </h3>
                  <button
                    onClick={() => {
                      setShowConnectorModal(false);
                      resetConnectorForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Connector Type
                    </label>
                    <select
                      value={connectorFormData.type}
                      onChange={(e) =>
                        setConnectorFormData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {connectorTypes.map((type) => (
                        <option key={type.type} value={type.type}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Connector Name
                    </label>
                    <input
                      type="text"
                      value={connectorFormData.name}
                      onChange={(e) =>
                        setConnectorFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Production AWS Account"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sync Schedule
                    </label>
                    <select
                      value={connectorFormData.sync_schedule}
                      onChange={(e) =>
                        setConnectorFormData((prev) => ({
                          ...prev,
                          sync_schedule: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="manual">Manual Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Configuration (JSON)
                    </label>
                    <textarea
                      rows={4}
                      value={JSON.stringify(connectorFormData.config, null, 2)}
                      onChange={(e) => {
                        try {
                          const config = JSON.parse(e.target.value);
                          setConnectorFormData((prev) => ({
                            ...prev,
                            config,
                          }));
                        } catch (error) {
                          // Invalid JSON, keep the text for user to fix
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder='{"api_key": "your-key", "region": "us-east-1"}'
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowConnectorModal(false);
                      resetConnectorForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateConnector}
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Create Connector
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Asset Detail Modal */}
      <AnimatePresence>
        {showAssetDetailModal && selectedAsset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Asset Details
                  </h3>
                  <button
                    onClick={() => {
                      setShowAssetDetailModal(false);
                      setSelectedAsset(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                {selectedAsset.asset && (
                  <div className="space-y-6">
                    {/* Asset Overview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </label>
                          <p className="text-sm text-gray-900 font-medium">
                            {selectedAsset.asset.name}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </label>
                          <p className="text-sm text-gray-900">
                            {selectedAsset.asset.type}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                          </label>
                          <p className="text-sm text-gray-900">
                            {selectedAsset.asset.category}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Owner
                          </label>
                          <p className="text-sm text-gray-900">
                            {selectedAsset.asset.owner}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Linked Risks */}
                    {selectedAsset.linked_risks &&
                      selectedAsset.linked_risks.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            Linked Risks
                          </h4>
                          <div className="space-y-2">
                            {selectedAsset.linked_risks.map((risk) => (
                              <div
                                key={risk.id}
                                className="bg-red-50 border border-red-200 rounded-lg p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-red-900">
                                    {risk.title}
                                  </span>
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCriticalityColor(
                                      risk.severity,
                                    )}`}
                                  >
                                    {risk.severity}
                                  </span>
                                </div>
                                <p className="text-xs text-red-700 mt-1">
                                  {risk.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Sync History */}
                    {selectedAsset.sync_history &&
                      selectedAsset.sync_history.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            Sync History
                          </h4>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {selectedAsset.sync_history.map((log, index) => (
                              <div
                                key={index}
                                className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                              >
                                <div className="flex items-center justify-between text-xs">
                                  <span className="font-medium text-gray-700">
                                    {log.action}
                                  </span>
                                  <span className="text-gray-500">
                                    {new Date(
                                      log.performed_at,
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  By: {log.performed_by}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Metadata */}
                    {selectedAsset.asset.metadata &&
                      Object.keys(selectedAsset.asset.metadata).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">
                            Metadata
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                              {JSON.stringify(
                                selectedAsset.asset.metadata,
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AssetManagement;
